import { useEffect, useState } from "react";
import {
  DEFAULT_BUYER_INTEREST_RANGE,
  DEMAND_SIGNAL_LABEL_KEYS,
  trackBuyerInterestEvent,
} from "../../constants/automapDealerBuyerInterestConstants";
import { useDealerPortalIntl } from "../../hooks/useDealerPortalIntl";
import { fetchDealerListingBuyerInterest } from "../../services/automapDealerBuyerInterestApi";

const INSIGHT_KEYS = {
  strongSaveActivity: "buyerInterest.insights.strongSaveActivity",
  watchingForLowerPrice: "buyerInterest.insights.watchingForLowerPrice",
  highSavesLowContact: "buyerInterest.insights.highSavesLowContact",
  confirmAvailability: "buyerInterest.insights.confirmAvailability",
};

export default function DealerListingBuyerInterestSection({ dealerId, listingId, range = DEFAULT_BUYER_INTEREST_RANGE }) {
  const { t } = useDealerPortalIntl();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    fetchDealerListingBuyerInterest(dealerId, listingId, { range })
      .then((payload) => {
        if (!cancelled) setData(payload);
      })
      .catch((err) => {
        if (!cancelled) {
          setData(null);
          setError(err.message || t("buyerInterest.loadError"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dealerId, listingId, range, t]);

  if (loading) {
    return (
      <section className="dealer-panel">
        <h3>{t("buyerInterest.listingSectionTitle")}</h3>
        <p className="muted">{t("common.loading")}</p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="dealer-panel">
        <h3>{t("buyerInterest.listingSectionTitle")}</h3>
        <p className="muted">{error}</p>
      </section>
    );
  }

  if (!data) return null;

  const counts = data.counts || {};
  const insightKeys = data.insightKeys || [];
  const demandLabelKey = DEMAND_SIGNAL_LABEL_KEYS[data.demandSignal] || DEMAND_SIGNAL_LABEL_KEYS.no_data;

  return (
    <section className="dealer-panel">
      <h3>{t("buyerInterest.listingSectionTitle")}</h3>
      <p className="dealer-panel__footnote muted">{t("buyerInterest.privacyNote")}</p>
      <div className="dealer-kpi-grid dealer-kpi-grid--compact">
        <article className="dealer-kpi-card">
          <span className="dealer-kpi-card__label">{t("buyerInterest.likes")}</span>
          <strong className="dealer-kpi-card__value">{counts.likes?.label ?? "0"}</strong>
        </article>
        <article className="dealer-kpi-card">
          <span className="dealer-kpi-card__label">{t("buyerInterest.compares")}</span>
          <strong className="dealer-kpi-card__value">{counts.compares?.label ?? "0"}</strong>
        </article>
        <article className="dealer-kpi-card">
          <span className="dealer-kpi-card__label">{t("buyerInterest.watchers")}</span>
          <strong className="dealer-kpi-card__value">{counts.watches?.label ?? "0"}</strong>
        </article>
      </div>
      <p className="muted">
        {t("buyerInterest.demandLabel")}: {t(demandLabelKey)}
        {data.likeTrend === "rising" ? ` · ${t("buyerInterest.risingTrend")}` : ""}
      </p>
      {insightKeys.length > 0 ? (
        <ul>
          {insightKeys.map((key) => (
            <li key={key}>{t(INSIGHT_KEYS[key] || key)}</li>
          ))}
        </ul>
      ) : (
        <p className="muted">{t("buyerInterest.belowThreshold")}</p>
      )}
    </section>
  );
}
